import { useIntl } from '@cultureamp/i18n-react-intl';
import React from 'react';
import { EnpsElevenPointScaleRating } from '../types';
import styles from './ElevenPointRatingScale.scss';
import { ElevenPointRatingScaleAnimation } from './ElevenPointRatingScaleAnimation';

type ElevenPointRatingScaleItemProps = {
  rating: EnpsElevenPointScaleRating;
  isFirstItem: boolean;
  isLastItem: boolean;
  isSelectedItem: boolean;
  colorSchema: string;
  onItemSelect: (value: EnpsElevenPointScaleRating) => void;
};

export const ElevenPointRatingScaleItem = ({
  rating,
  isFirstItem,
  isLastItem,
  isSelectedItem,
  colorSchema,
  onItemSelect,
}: ElevenPointRatingScaleItemProps) => {
  const { formatMessage } = useIntl();
  const isBlueSchema = colorSchema === 'blue';
  const likertColor = isBlueSchema ? 'bg-blue-500' : 'bg-red-500';
  const bgHoverLikertColor = isBlueSchema
    ? `${styles.hoverBlue}`
    : `${styles.hoverRed}`;
  const handleRadioClick = (rating: EnpsElevenPointScaleRating) => {
    onItemSelect(rating);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    rating: EnpsElevenPointScaleRating
  ) => {
    if (
      (event.type === 'keydown' && event.key === 'Enter') ||
      event.key === ' '
    ) {
      event.preventDefault();
      onItemSelect(rating);
    }
  };
  return (
    <div
      key={rating}
      className={`${
        styles.elevenRatingScaleItem
      } max-sm:h-[36px] max-sm:w-[36px] h-[35px] w-[38px] border cursor-pointer border-solid max-sm:rounded-full rounded-none border-gray-500 flex justify-center items-center ${
        isFirstItem ? `${styles.firstItemBorder}` : ''
      } ${isLastItem ? `${styles.lastItemBorder}` : ''} ${bgHoverLikertColor} ${
        isSelectedItem ? likertColor : ''
      }`}
    >
      <input
        id={`scale-point-${rating}`}
        type="radio"
        aria-label={
          isFirstItem || isLastItem
            ? formatMessage(
                {
                  id: 'ENPSElevenPointRatingScale.screenReader.RatingContext',
                  defaultMessage:
                    'rating {rating} of 10. 0 being not at all likely, 10 being extremely likely',
                  description:
                    "This text is for screen readers only. It provides context for the 0 to 10 rating on a scale, which visually corresponds to 'Not at all likely and Extremely likely' labels.",
                },
                { rating: `${rating}` }
              )
            : formatMessage(
                {
                  id: 'ENPSElevenPointRatingScaleItem.InputAriaLabel',
                  defaultMessage: 'rating {rating} of 10',
                  description:
                    'Provides screen reader users with context for the rating value they are currently on. The {rating} placeholder represents a number from 0 to 10.',
                },
                { rating: `${rating}` }
              )
        }
        name="eleven-rating-scale-point"
        value={`${rating}`}
        className={`z-10 appearance-none absolute rounded-none cursor-pointer !h-[35px] !w-[38px] focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-blue-500 focus-visible:!outline-offset-1 ${
          isFirstItem ? `${styles.firstItemBorder}` : ''
        } ${isLastItem ? `${styles.lastItemBorder}` : ''}`}
        checked={isSelectedItem}
        aria-checked={isSelectedItem}
        onClick={() => handleRadioClick(rating)}
        onKeyDown={(event) => handleKeyDown(event, rating)}
      />
      <label
        htmlFor={`scale-point-${rating}`}
        aria-hidden="true"
        className={`appearance-none z-10 p-0 w-fit font-family-paragraph text-black ${
          isSelectedItem ? '!text-white opacity-100' : ''
        }`}
      >
        {rating}
      </label>

      <ElevenPointRatingScaleAnimation
        isFirstItem={isFirstItem}
        isLastItem={isLastItem}
        isSelectedItem={isSelectedItem}
        likertColor={likertColor}
      ></ElevenPointRatingScaleAnimation>
    </div>
  );
};
